import { z } from "zod";

const openlyErrorTypeValues = [
  "WEBHOOK_VERIFICATION_FAILED",
  "CLAIM_FETCH_FAILED",
  "CLAIM_DATA_INCOMPLETE",
  "TOKEN_FETCH_FAILED",
  "COMPANY_ID_NOT_SUPPORTED",
  "MISSING_PDF_BODY",
  "UNPARSEABLE_PDF",
  "NO_ROOFPLANS_FOUND",
  "ASSIGNMENT_NOT_FOUND",
  "MISSING_EXTERNAL_IDS",
  "MULTIPLE_RSG_ASSIGNMENTS",
  "MULTIPLE_EST_ASSIGNMENTS",
  "INVALID_EXTERNAL_ID",
  "PDF_PARSER_AUTH_FAILED",
  "OVERHEAD_PROFIT_UNSUPPORTED",
  "STRUCTURES_DO_NOT_COVER_SUBTOTAL",
  "DISCOUNT_DOES_NOT_RECONCILE_TO_RCV",
  "ESTIMATE_ITEM_DESCRIPTION_CLAMPED",
] as const;

const openlyErrorTypeSchema = z.enum(openlyErrorTypeValues);
const OpenlyErrorType = openlyErrorTypeSchema.enum;
type OpenlyErrorType = z.infer<typeof openlyErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type OpenlyErrors =
  | {
      errorType: typeof OpenlyErrorType.WEBHOOK_VERIFICATION_FAILED;
      data: { reason: string };
    }
  | {
      errorType: typeof OpenlyErrorType.CLAIM_FETCH_FAILED;
      data: { claimUniqueId: string };
    }
  | {
      errorType: typeof OpenlyErrorType.CLAIM_DATA_INCOMPLETE;
      data: { claimUniqueId: string; missingFields: string[] };
    }
  | {
      errorType: typeof OpenlyErrorType.TOKEN_FETCH_FAILED;
      data: { httpStatusCode: number | undefined };
    }
  | {
      errorType: typeof OpenlyErrorType.COMPANY_ID_NOT_SUPPORTED;
      data: { companyId: string };
    }
  | {
      errorType: typeof OpenlyErrorType.MISSING_PDF_BODY;
      data: undefined;
    }
  | {
      errorType: typeof OpenlyErrorType.UNPARSEABLE_PDF;
      data: { reason: string };
    }
  | {
      errorType: typeof OpenlyErrorType.NO_ROOFPLANS_FOUND;
      data: { claimNumber: string };
    }
  | {
      errorType: typeof OpenlyErrorType.ASSIGNMENT_NOT_FOUND;
      data: undefined;
    }
  | {
      errorType: typeof OpenlyErrorType.MISSING_EXTERNAL_IDS;
      data: undefined;
    }
  | {
      errorType: typeof OpenlyErrorType.MULTIPLE_RSG_ASSIGNMENTS;
      data: { assignmentIds: string[]; claimIds: string[] };
    }
  | {
      errorType: typeof OpenlyErrorType.MULTIPLE_EST_ASSIGNMENTS;
      data: { assignmentIds: string[]; claimIds: string[] };
    }
  | {
      errorType: typeof OpenlyErrorType.INVALID_EXTERNAL_ID;
      data: undefined;
    }
  | {
      errorType: typeof OpenlyErrorType.PDF_PARSER_AUTH_FAILED;
      data: undefined;
    }
  | {
      errorType: typeof OpenlyErrorType.OVERHEAD_PROFIT_UNSUPPORTED;
      data: { overhead: number; profit: number };
    }
  | {
      errorType: typeof OpenlyErrorType.STRUCTURES_DO_NOT_COVER_SUBTOTAL;
      data: { structureLineTotal: number; flatLineTotal: number; lineItemSubtotal: number };
    }
  | {
      errorType: typeof OpenlyErrorType.DISCOUNT_DOES_NOT_RECONCILE_TO_RCV;
      data: { lineItemSubtotal: number; totalTax: number; discount: number; replacementCostValue: number };
    }
  | {
      errorType: typeof OpenlyErrorType.ESTIMATE_ITEM_DESCRIPTION_CLAMPED;
      data: { assignmentId: string; clampedLineNumbers: string; longestDescriptionLength: number };
    };

export const openlyErrorDetails: Record<OpenlyErrorType, ErrorDetails> = {
  [OpenlyErrorType.WEBHOOK_VERIFICATION_FAILED]: {
    sentryDescription: "Openly webhook verification failed",
  },
  [OpenlyErrorType.CLAIM_FETCH_FAILED]: {
    sentryDescription: "Failed to fetch claim details from Symbility",
  },
  [OpenlyErrorType.CLAIM_DATA_INCOMPLETE]: {
    sentryDescription: "Claim fetched from Symbility but missing required fields",
  },
  [OpenlyErrorType.TOKEN_FETCH_FAILED]: {
    sentryDescription: "Failed to fetch Symbility OAuth access token",
  },
  [OpenlyErrorType.COMPANY_ID_NOT_SUPPORTED]: {
    sentryDescription: "Symbility CompanyID not mapped to a supported organization",
  },
  [OpenlyErrorType.MISSING_PDF_BODY]: {
    sentryDescription: "PDF parser invoked without a PDF body",
  },
  [OpenlyErrorType.UNPARSEABLE_PDF]: {
    sentryDescription: "PDF could not be parsed — no claim number or invalid format",
  },
  [OpenlyErrorType.NO_ROOFPLANS_FOUND]: {
    sentryDescription: "PDF parsed but no ROOFPLAN sections found",
  },
  [OpenlyErrorType.ASSIGNMENT_NOT_FOUND]: {
    sentryDescription: "No Openly assignment found for the given external ID",
  },
  [OpenlyErrorType.MISSING_EXTERNAL_IDS]: {
    sentryDescription: "Inbound status update had no externalIds",
  },
  [OpenlyErrorType.MULTIPLE_RSG_ASSIGNMENTS]: {
    sentryDescription: "Multiple RSG assignments found for claim — expected exactly one",
  },
  [OpenlyErrorType.MULTIPLE_EST_ASSIGNMENTS]: {
    sentryDescription: "Multiple EST assignments found for claim with no RSG fallback",
  },
  [OpenlyErrorType.INVALID_EXTERNAL_ID]: {
    sentryDescription: "External ID is not a valid composite key (expected claimGUID&assignmentId)",
  },
  [OpenlyErrorType.PDF_PARSER_AUTH_FAILED]: {
    sentryDescription: "PDF parser authentication failed",
  },
  [OpenlyErrorType.OVERHEAD_PROFIT_UNSUPPORTED]: {
    sentryDescription: "Openly PDF carries overhead/profit, which the recomputed estimate total cannot place",
  },
  [OpenlyErrorType.STRUCTURES_DO_NOT_COVER_SUBTOTAL]: {
    sentryDescription: "Openly PDF structure line items do not sum to the printed line-item subtotal",
  },
  [OpenlyErrorType.DISCOUNT_DOES_NOT_RECONCILE_TO_RCV]: {
    sentryDescription: "Openly PDF subtotal + tax - discount does not equal the printed replacement cost value",
  },
  [OpenlyErrorType.ESTIMATE_ITEM_DESCRIPTION_CLAMPED]: {
    sentryDescription: "Openly estimate item description exceeded the column limit and was clamped before write",
  },
};
